<template>
  <div class="flex items-start gap-4">
    <!-- 点击头像即可上传：hover 显示相机遮罩，上传中显示 loading -->
    <AvatarCropUpload :disabled="uploading" @select="handleUpload">
      <div
        class="group relative cursor-pointer rounded-full"
        role="button"
        tabindex="0"
        :aria-label="t('shell.account.avatarUploadNew')"
      >
        <UserAvatar :src="preview" :size="80" />
        <div
          class="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 transition-opacity"
          :class="uploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
        >
          <a-spin v-if="uploading" />
          <Camera v-else class="w-5 h-5 text-white" aria-hidden="true" />
        </div>
      </div>
    </AvatarCropUpload>

    <div class="flex flex-col items-start gap-2">
      <AvatarCropUpload :disabled="uploading" @select="handleUpload">
        <a-button :loading="uploading">
          <div class="inline-flex items-center gap-1">
            <Upload class="w-4 h-4" aria-hidden="true" />
            {{ preview ? t('shell.account.avatarUpload') : t('shell.account.avatarUploadNew') }}
          </div>
        </a-button>
      </AvatarCropUpload>

      <a-button v-if="canUndo" type="text" :disabled="uploading" @click="handleUndo">
        <div class="inline-flex items-center gap-1">
          <Undo2 class="w-4 h-4" aria-hidden="true" />
          {{ t('shell.account.avatarUndo') }}
        </div>
      </a-button>

      <a-button v-if="preview" type="text" danger :disabled="uploading" @click="handleReset">
        <div class="inline-flex items-center gap-1">
          <Trash2 class="w-4 h-4" aria-hidden="true" />
          {{ t('shell.account.avatarReset') }}
        </div>
      </a-button>

      <div class="text-xs text-gray-400 dark:text-gray-500">
        {{ preview ? t('shell.account.avatarFormatHint') : t('shell.account.avatarEmptyHint') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { message, Modal } from 'ant-design-vue'
import { Camera, Trash2, Undo2, Upload } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { uploadFile } from '../../api/upload'
import { updateAvatar } from '../../api/user/setting'
import { useI18n } from '../../composables/useI18n'
import AvatarCropUpload from '../AvatarCropUpload.vue'
import UserAvatar from '../UserAvatar.vue'

const props = defineProps<{ currentAvatar: string }>()
const emit = defineEmits<{ updated: [value: string] }>()
const { t } = useI18n()

const preview = ref(props.currentAvatar)
// One-level undo: the avatar that was replaced by the latest upload / reset
const previous = ref('')
const uploading = ref(false)
const canUndo = computed(() => !!previous.value && previous.value !== preview.value)

watch(
  () => props.currentAvatar,
  (value) => (preview.value = value),
)

async function handleUpload(file: File) {
  uploading.value = true
  try {
    const url = await uploadFile(file, { bizType: 'userAvatar' })
    await save(url)
    message.success(t('shell.account.avatarUploadSuccess'))
  } catch (e: any) {
    message.error(e?.message || t('shell.account.avatarUploadFailed'))
  } finally {
    uploading.value = false
  }
}

// Reset is destructive: confirm before removing the current avatar
function handleReset() {
  Modal.confirm({
    title: t('shell.account.avatarResetTitle'),
    content: t('shell.account.avatarResetDesc'),
    okText: t('shell.account.avatarReset'),
    cancelText: t('common.cancel'),
    okButtonProps: { danger: true },
    onOk: async () => {
      try {
        await save('')
        message.success(t('shell.account.avatarResetSuccess'))
      } catch (e: any) {
        message.error(e?.message || t('shell.account.updateFailed'))
      }
    },
  })
}

// Undo the failed upload: restore the avatar that was replaced
async function handleUndo() {
  const target = previous.value
  if (!target) return
  uploading.value = true
  try {
    await updateAvatar(target)
    preview.value = target
    previous.value = ''
    emit('updated', target)
    message.success(t('shell.account.avatarUndoSuccess'))
  } catch (e: any) {
    message.error(e?.message || t('shell.account.avatarUploadFailed'))
  } finally {
    uploading.value = false
  }
}

async function save(url: string) {
  const replaced = preview.value
  await updateAvatar(url)
  preview.value = url
  previous.value = replaced && replaced !== url ? replaced : ''
  emit('updated', url)
}
</script>
